export const CHUNK_SIZE = 2 * 1024 * 1024; // 2 MB per chunk

async function extractErrorMessage(response) {
  try {
    const text = await response.text();
    if (!text) return `${response.status} ${response.statusText}`;
    try {
      const data = JSON.parse(text);
      return (
        data?.error ||
        data?.message ||
        data?.detail ||
        (typeof data === "string" ? data : JSON.stringify(data))
      );
    } catch {
      return text;
    }
  } catch {
    return "Request failed";
  }
}

/**
 * Upload files in 2MB chunks per the v1 cards API.
 * Returns batch_id from the server once the first chunk succeeds.
 */
export async function uploadFilesInChunks({
  files,
  batchName,
  token,
  apiBaseUrl,
  onChunkProgress,
}) {
  let batchId = null;
  let chunksUploaded = 0;
  const totalChunksAll = files.reduce(
    (sum, file) => sum + Math.ceil(file.size / CHUNK_SIZE),
    0
  );
  const totalFiles = files.length;

  for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
    const file = files[fileIndex];
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append("chunk", chunk);
      formData.append("file_name", file.name);
      formData.append("chunk_index", String(chunkIndex));
      formData.append("total_chunks", String(totalChunks));
      formData.append("file_index", String(fileIndex));
      formData.append("total_files", String(totalFiles));
      formData.append("batch_name", batchName);
      if (batchId != null) {
        formData.append("batch_id", String(batchId));
      }

      const response = await fetch(
        `${apiBaseUrl}/api/v1/cards/upload-chunk/`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );

      if (!response.ok) {
        const apiMessage = await extractErrorMessage(response);
        throw new Error(
          apiMessage ||
            `Chunk ${chunkIndex + 1}/${totalChunks} upload failed for ${file.name}`
        );
      }

      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (data?.batch_id != null) {
        batchId = data.batch_id;
      }

      chunksUploaded += 1;
      onChunkProgress?.({
        chunksUploaded,
        totalChunks: totalChunksAll,
        fileName: file.name,
        fileChunksUploaded: chunkIndex + 1,
        fileChunksTotal: totalChunks,
        serverProgress: data?.progress,
        batchId,
      });
    }
  }

  return { batchId };
}

/**
 * Trigger assembly and background processing after all chunks are uploaded.
 */
export async function finalizeCardUpload({
  batchName,
  defaultMetadata,
  titleBuilderData,
  token,
  apiBaseUrl,
}) {
  const payload = {
    batch_name: batchName,
    titleBuilderData: titleBuilderData ?? [],
  };

  if (defaultMetadata && Object.keys(defaultMetadata).length > 0) {
    payload.default_metadata = defaultMetadata;
  }

  const response = await fetch(
    `${apiBaseUrl}/api/v1/cards/finalize-upload/`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );

  let data = {};
  try {
    const text = await response.text();
    if (text) data = JSON.parse(text);
  } catch {
    data = {};
  }

  const accepted = response.ok || response.status === 202;
  if (!accepted) {
    const apiMessage =
      data?.error ||
      data?.message ||
      data?.detail ||
      `${response.status} ${response.statusText}`;
    throw new Error(apiMessage || "Failed to finalize upload");
  }

  return data;
}
